import React, { useState } from "react";
import { Sheet, SheetContent } from "components/ui/sheet";
import { Avatar, AvatarFallback } from "components/ui/avatar";
import { ChevronLeft, Users } from "lucide-react";
import localization from "services/localization";
import { useThemeStore } from "context/useThemeStore";

const SideMenu = ({
  role,
  classroomId,
  students,
  messages = [],
  selectedHistory,
  openStudentRanking,
  isTablet,
}) => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const { theme } = useThemeStore();

  const SheetHeader = ({ studentsCount }) => (
    <div className="border-b border-gray-200 ">
      <div className="flex items-center gap-2 p-2 ml-2 pt-4">
        <Users className="w-4 h-4 text-[var(--primary-icon)]" />
        <span className="text-sm font-semibold page-title">
          {localization.students || "Students"} ({studentsCount})
        </span>
      </div>
    </div>
  );

  const getAverageRating = (ratingObj) => {
    if (!ratingObj || typeof ratingObj !== "object") return 0;

    const values = Object.values(ratingObj).filter(
      (v) => typeof v === "number",
    );

    if (values.length === 0) return 0;

    return values.reduce((sum, v) => sum + v, 0) / values.length;
  };

  const getClassRoomStudents = () => {
    if (!(classroomId && role === "teacher")) return [];
    const subMembers = selectedHistory?.prompt_data?.sub_members || [];

    return subMembers
      .map((studentId) => {
        const student = students.find((s) => s.id === studentId);
        if (!student) return null;

        const studentMessages = messages.filter(
          (m) => m.user_id === student.id,
        );

        const averageRating = studentMessages.length
          ? studentMessages
              .map((m) => getAverageRating(m.prompt_health?.rating))
              .reduce((sum, r) => sum + r, 0) / studentMessages.length
          : 0;

        return {
          ...student,
          averageRating,
          isFlagged: studentMessages.length > 0 && averageRating <= 2,
        };
      })
      .filter(Boolean);
  };

  const studentsList = getClassRoomStudents();

  // getInitials: returns first two letters or fallback "U"
  const getInitials = (name) => {
    if (!name || typeof name !== "string") return "U";

    // Split by space or underscore
    const parts = name.split(/[\s_]+/).filter(Boolean);

    if (parts.length === 0) return name.slice(0, 2).toUpperCase();
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

    return (parts[0][0] + parts[1][0]).toUpperCase();
  };

  const getHoverClass = (isFlagged, theme) => {
    if (isFlagged) {
      return theme === "dark" ? "hover:bg-red-800/30" : "hover:bg-red-100";
    }

    return theme === "dark" ? "hover:bg-gray-700/60" : "hover:bg-gray-100";
  };

  const categoryMenu = (
    <div className="flex flex-col h-full min-h-0 overflow-y-auto bg-[var(--secondary-bg)]">
      {role === "teacher" && (
        <div className="flex flex-col">
          <SheetHeader studentsCount={studentsList.length} />

          {studentsList.length === 0 && (
            <p className="text-gray-500 text-sm p-2">
              {localization.pleaseselectAHistory || "Please select a history."}
            </p>
          )}

          <div className="flex flex-col p-2">
            {studentsList.map((student) => (
              <button
                key={student.id}
                className={`flex items-center gap-2 p-2 rounded-lg transition-colors duration-200 ${getHoverClass(
                  student.isFlagged,
                  theme,
                )}`}
                onClick={() => openStudentRanking(student.id, student.name)}
              >
                <div
                  className={`relative ${
                    student.isFlagged ? "ring-2 ring-red-500 rounded-full" : ""
                  }`}
                >
                  <Avatar className="h-8 w-8 cursor-pointer">
                    <AvatarFallback className="text-sm">
                      {getInitials(student.name)}
                    </AvatarFallback>
                  </Avatar>
                </div>
                <span className="page-title text-sm font-medium">
                  {student.name}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const ChevronButton = () => (
    <button
      className="fixed top-1/2 right-0 z-50 w-12 h-14 flex items-center justify-center rounded-l-lg transition-colors duration-200"
      onClick={() => setSheetOpen(true)}
      style={{ color: "var(--primary-icon)" }}
      onMouseEnter={(e) => (e.currentTarget.style.color = "var(--sub-icon)")}
      onMouseLeave={(e) =>
        (e.currentTarget.style.color = "var(--primary-icon)")
      }
    >
      <ChevronLeft className="w-6 h-6" />
    </button>
  );

  return (
    <>
      {isTablet ? (
        <>
          {!sheetOpen && <ChevronButton />}
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen} side="right">
            <SheetContent side="right" className="p-0">
              {categoryMenu}
            </SheetContent>
          </Sheet>
        </>
      ) : (
        <aside className="hidden lg:flex flex-col min-h-0 overflow-y-auto bg-[var(--card-bg)] w-[270px]">
          {categoryMenu}
        </aside>
      )}
    </>
  );
};

export default SideMenu;
